import { useCallback, useEffect, useState } from 'react';

import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { useI18n } from 'i18n/hooks/useI18n';
import { cmsApiCreateReaction } from 'modules/cms/api/reactions/cmsApiCreateReaction';
import { cmsApiDeleteReaction } from 'modules/cms/api/reactions/cmsApiDeleteReaction';
import { useCmsApiFetchReaction } from 'modules/cms/api/reactions/cmsApiFetchReaction';
import type { CmsApiReactionType } from 'modules/cms/api/reactions/types/CmsApiReactionType';
import type { CmsApiPagePost } from 'modules/cms/api/types/CmsApiPage';
import { userDashboardApiEnableProfileFeatures } from 'modules/userDashboard/api//userDashboardApiEnableProfileFeatures';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { scrollIntoView } from 'utils/scrollIntoView';
import { showModal } from 'zustand-stores/modalStore';
import {
  setPreSignupReaction,
  usePreSignupReaction,
} from 'zustand-stores/preSignupReactionStore';

import { CmsReactionButton } from './CmsReactionButton';
import {
  CmsReactionsButtonsContainer,
  CmsReactionsContainer,
  CmsReactionsUserEngagementBar,
  CmsReactionsWrapper,
} from './CmsReactions.styled';
import { CommentButton } from './CmsReactionsCommentButton';
import { CmsReactionsContext } from './CmsReactionsContext';
import { ShareButton } from './CmsReactionsShareButton';
import { CmsReactionsSummary } from './CmsReactionsSummary';
import { CmsReactionsUserAvatar } from './CmsReactionsUserAvatar';

type Props = {
  content: CmsApiPagePost;
  user: ApiUser | null | undefined;
};

const defaultReactionContext = {
  reactions: { LIKE: 0, LOVE: 0, INSPIRED: 0 },
  latestReactions: [],
  ownReaction: null,
  totalReactions: 0,
};

function CmsReactionsWithReactionsDisabled({
  content,
}: Pick<Props, 'content'>) {
  return (
    <CmsReactionsContainer id="cms-reactions" data-qa-id="cms-reactions">
      <CmsReactionsWrapper>
        <CmsReactionsUserEngagementBar>
          <CmsReactionsUserAvatar />
          <CmsReactionsButtonsContainer>
            {content.hasSocialSharing ? (
              <ShareButton content={content} />
            ) : null}
            {content.enableComments ? <CommentButton /> : null}
          </CmsReactionsButtonsContainer>
        </CmsReactionsUserEngagementBar>
      </CmsReactionsWrapper>
    </CmsReactionsContainer>
  );
}

export function CmsReactionsWithReactionsEnabled({ content, user }: Props) {
  const { i18n } = useI18n();

  const [reactionContext = defaultReactionContext, { loading, updateCache }] =
    useCmsApiFetchReaction(content.id);

  const { preSignupReaction } = usePreSignupReaction();

  const [unfinishedReaction, setUnfinishedReaction] =
    useState<CmsApiReactionType | null>(null);

  const handleReactionClick = useCallback(
    (reactionType: CmsApiReactionType) => {
      if (!user) {
        showModal('EMAIL_SIGNUP', {
          title: i18n('Sign up to add a reaction'),
          onSignup: () => {
            setPreSignupReaction({ postId: content.id, reactionType });
          },
          onLogin: () => {
            setPreSignupReaction({ postId: content.id, reactionType });
          },
          requirePassword: true,
        });
        return;
      }

      if (!user.hasIdealistProfileFeature) {
        userDashboardApiEnableProfileFeatures().then(() => {
          showModal('CREATE_USER_PROFILE', {});
          setUnfinishedReaction(reactionType);
        });
        return;
      }

      if (!user.hasPublishedIdealistProfile) {
        showModal('CREATE_USER_PROFILE', {});
        setUnfinishedReaction(reactionType);
        return;
      }

      if (
        !reactionContext.ownReaction ||
        reactionContext.ownReaction.reactionType !== reactionType
      ) {
        cmsApiCreateReaction(content.id, reactionType).then(
          (updatedReactionContext) => {
            updateCache(updatedReactionContext);
          },
        );
        trackClicked('Added Reaction', { reaction_type: reactionType });
      } else {
        cmsApiDeleteReaction(content.id).then((updatedReactionContext) => {
          updateCache(updatedReactionContext);
        });
        trackClicked('Removed Reaction', { reaction_type: reactionType });
      }
    },
    [i18n, content.id, reactionContext.ownReaction, updateCache, user],
  );

  useEffect(() => {
    if (
      !loading &&
      user?.emailVerified &&
      preSignupReaction &&
      preSignupReaction.postId === content.id
    ) {
      setUnfinishedReaction(preSignupReaction.reactionType);
      const element = document.getElementById('cms-reactions');
      if (element) {
        scrollIntoView(element);
      }
    }
  }, [content.id, loading, preSignupReaction, user?.emailVerified]);

  useEffect(() => {
    if (!loading && unfinishedReaction && user?.hasPublishedIdealistProfile) {
      if (!reactionContext.ownReaction) {
        handleReactionClick(unfinishedReaction);
      }
      setPreSignupReaction(null);
      setUnfinishedReaction(null);
    }
  }, [
    handleReactionClick,
    unfinishedReaction,
    user?.hasPublishedIdealistProfile,
    reactionContext.ownReaction,
    loading,
  ]);

  if (loading) return null;

  return (
    <CmsReactionsContext.Provider value={reactionContext}>
      <CmsReactionsContainer id="cms-reactions" data-qa-id="cms-reactions">
        <CmsReactionsWrapper>
          <CmsReactionsSummary contentId={content.id} />
          <CmsReactionsUserEngagementBar>
            <CmsReactionsUserAvatar />
            <CmsReactionsButtonsContainer>
              <CmsReactionButton
                onReactionClick={handleReactionClick}
                key={user?.id ?? 'logged out'}
              />
              {content.hasSocialSharing ? (
                <ShareButton content={content} />
              ) : null}
              {content.enableComments ? <CommentButton /> : null}
            </CmsReactionsButtonsContainer>
          </CmsReactionsUserEngagementBar>
        </CmsReactionsWrapper>
      </CmsReactionsContainer>
    </CmsReactionsContext.Provider>
  );
}

export function CmsReactions({ content, user }: Props) {
  if (content.hasReactions)
    return <CmsReactionsWithReactionsEnabled content={content} user={user} />;

  if (content.hasSocialSharing)
    return <CmsReactionsWithReactionsDisabled content={content} />;

  return null;
}
